/**
 * 인증 관련 전역 상태를 관리하는 Store
 * - 회원가입
 * - 소셜 로그인 처리
 * - 이메일 로그인
 * - 로그아웃
 * - 회원 탈퇴
 */
import { useAccountStore } from '@/features/account/stores/accountStore';
import { getCachedSession, invalidateSessionCache } from '@/features/auth/utils/sessionCache';
import logger from '@/utils/logger';
import axios, { AxiosError, HttpStatusCode } from 'axios';
import { signIn, signOut } from 'next-auth/react';
import { toast } from 'sonner';
import { create } from 'zustand';
import { authService } from '../services/authService';
import { SignUpFormData } from '../types/api/auth';
import { AuthProvider } from '../types/enum';

/**
 * 인증 Store의 상태 및 액션 타입 정의
 */
interface AuthStore {
  hasProvider: () => Promise<boolean>;

  handleSignUp: (data: SignUpFormData) => Promise<void>; // 회원가입
  handleProviderSignIn: (
    // 소셜 로그인 처리
    provider: AuthProvider,
    signInId: string,
    token: string,
  ) => Promise<void>;
  signInEmail: (signInId: string, password?: string) => Promise<void>; // 이메일 로그인
  signOut: () => Promise<void>; // 로그아웃
  deleteAccount: () => Promise<void>; // 회원 탈퇴
}

const requestSignIn = async (
  provider: AuthProvider,
  signInId: string,
  token?: string,
  password?: string,
) => {
  return await signIn('credentials', {
    provider: provider.toLowerCase(),
    signInId: signInId,
    token: token,
    password: password,
    redirect: false,
  }).then(async (result) => {
    logger.info('++ result', result);
    if (result?.error === 'CredentialsSignin') {
      toast.error('이메일 또는 비밀번호가 일치하지 않습니다.');
    }
    if (!result?.error) {
      return useAccountStore
        .getState()
        .onChangeUser(true)
        .finally(() => {
          return Promise.resolve();
        });
    }

    if (result.code?.toString() === 'undefined') {
      throw new Error('관리자에게 문의 부탁 드립니다.');
    }

    throw new Error(result.code);
  });
};

export const useAuthStore = create<AuthStore>(() => ({
  hasProvider: async () => {
    const session = await getCachedSession();
    logger.info('>> hasProvider session', session);
    return session?.user?.provider === AuthProvider.EMAIL;
  },

  /**
  /**
   * 회원가입
   * @param data - 회원가입 데이터
   */
  handleSignUp: async (data: SignUpFormData) => {
    logger.info('>> handleSignUp()', data);

    return await authService
      .signUp(data)
      .then(() => {
        return Promise.resolve();
      })
      .catch((error) => {
        logger.error('-- handleSignUp()', error);
        throw error;
      });
  },

  /**
   * 소셜 로그인 처리
   * @param provider - 소셜 로그인 제공자 (google 등)
   * @param signinId - 소셜 계정 ID
   * @param token - 소셜 로그인 토큰
   * @returns 로그인 성공
   * @throws 계정 없음 오류(404), 기타 로그인 오류
   */
  handleProviderSignIn: async (provider, signinId, token) => {
    logger.info('>> handleProviderSignIn()', provider, signinId, token);

    return await authService
      .checkDuplicateUser(provider, signinId)
      .then(() => {
        return Promise.reject(
          new AxiosError(
            '가입된 계정이 없습니다',
            undefined,
            undefined,
            undefined,
            {
              status: HttpStatusCode.NotFound,
              statusText: 'Not Found',
              data: null,
              headers: {},
              config: {},
            } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          ),
        );
      })
      .catch((error) => {
        if (
          axios.isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Conflict
        ) {
          logger.info('++ error: ', HttpStatusCode.Conflict);
          return requestSignIn(provider, signinId, token);
        } else {
          throw error;
        }
      });
  },

  /**
   * 이메일 로그인 처리
   * @param signInId - 로그인 ID
   * @param password - 비밀번호
   */
  signInEmail: async (signInId, password) => {
    logger.info('>> signInEmail()', signInId, password);

    return await requestSignIn(AuthProvider.EMAIL, signInId, undefined, password);
  },

  /**
   * 로그아웃 처리
   */
  signOut: async () => {
    logger.info('>> signOut()');

    return await signOut({
      redirect: true,
      callbackUrl: '/',
    }).then(() => {
      return useAccountStore
        .getState()
        .onChangeUser(false)
        .catch((error) => {
          logger.error('-- signOut()', error);
          throw error;
        })
        .finally(() => {
          return Promise.resolve();
        });
    });
  },

  /**
   * 회원 탈퇴 처리
   * - 현재 세션의 액세스 토큰으로 회원 탈퇴 API 호출
   * - 성공 시 로그아웃 처리
   */
  deleteAccount: async () => {
    logger.info('>> deleteAccount()');

    const session = await getCachedSession();
    const accessToken = session?.user?.accessToken;

    if (!accessToken) throw new Error('No access token found');

    return authService
      .deleteAccount(accessToken)
      .then(() => {
        // 세션 캐시 무효화
        invalidateSessionCache();
        useAuthStore.getState().signOut();
      })
      .catch((error) => {
        logger.error('-- deleteAccount()', error);
        throw error;
      });
  },
}));
